import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Grid, Button, Collapse, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadarChart from './RadarChart';
import MetricDots from './MetricDots';
import SaveBulletDialog from './SaveBulletDialog';

/**
 * Bullet point card with scores and visualization
 * 
 * Shows:
 * - Bullet type badge (STAR/CAR/APR)
 * - Overall score badge with color coding
 * - Bullet text
 * - Radar chart visualization
 * - Individual metric scores
 * - Goal alignment
 */
const BulletCard = ({ bullet, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const total = Object.values(bullet.scores).reduce((a, b) => a + b, 0);
  
  // Color coding based on total score
  const getScoreBadge = (score) => {
    if (score >= 13) return { label: 'Excellent', color: '#22c55e', bgcolor: '#dcfce7' };
    if (score >= 10) return { label: 'Good', color: '#3b82f6', bgcolor: '#dbeafe' };
    if (score >= 7) return { label: 'Fair', color: '#f59e0b', bgcolor: '#fef3c7' };
    return { label: 'Needs Work', color: '#ef4444', bgcolor: '#fee2e2' };
  };

  const scoreBadge = getScoreBadge(total);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      sx={{
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderColor: '#3b82f6'
        }
      }}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        {/* Header: Type, Score, and Expand Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={bullet.type}
              size="small"
              sx={{
                bgcolor: '#f1f5f9',
                color: '#475569',
                fontWeight: 600,
                fontSize: '11px'
              }}
            />
            <Chip
              label={`${total}/15 ${scoreBadge.label}`}
              size="small"
              sx={{
                bgcolor: scoreBadge.bgcolor,
                color: scoreBadge.color,
                fontWeight: 600,
                fontSize: '11px'
              }}
            />
          </Box>
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {/* Compressed View: Text + Chart Side by Side */}
        {!expanded && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Bullet Text - Takes most space */}
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                lineHeight: 1.6,
                color: '#1f2937',
                cursor: 'pointer'
              }}
              onClick={handleToggle}
            >
              {bullet.text}
            </Typography>
            
            {/* Small Radar Chart - Fixed width */}
            <Box 
              sx={{ 
                flexShrink: 0,
                cursor: 'pointer'
              }}
              onClick={handleToggle}
            >
              <RadarChart scores={bullet.scores} size={70} showScore={false} />
            </Box>
          </Box>
        )}

        {/* Expanded View: Full Text */}
        {expanded && (
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              lineHeight: 1.6,
              color: '#1f2937'
            }}
          >
            {bullet.text}
          </Typography>
        )}

        {/* Expanded View: Full Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            {/* Chart and Metrics */}
            <Grid container spacing={2}>
              {/* Left: Radar Chart */}
              <Grid item xs={5}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <RadarChart scores={bullet.scores} size={140} />
                </Box>
              </Grid>

              {/* Right: Metric List */}
              <Grid item xs={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MetricRow label="Action Strength" value={bullet.scores.actionStrength} />
                  <MetricRow label="Specificity" value={bullet.scores.specificity} />
                  <MetricRow label="Impact" value={bullet.scores.impact} />
                  <MetricRow label="Clarity" value={bullet.scores.clarity} />
                  <MetricRow label="Achievement" value={bullet.scores.achievementFocus} />
                </Box>
              </Grid>
            </Grid>

            {/* Goal Alignment */}
            {bullet.goalAlignment && (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                  Goal Alignment
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MetricDots value={bullet.goalAlignment.score} maxValue={5} />
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        bullet.goalAlignment.evidenceStrength === 'strong'
                          ? '#22c55e'
                          : bullet.goalAlignment.evidenceStrength === 'moderate'
                          ? '#3b82f6'
                          : '#f59e0b',
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }}
                  >
                    {bullet.goalAlignment.evidenceStrength}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Save Button */}
            {onSave && (
              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => setSaveDialogOpen(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    py: 0.75
                  }}
                >
                  Save to Profile
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Save Dialog */}
        <SaveBulletDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          bullet={bullet}
          onSaved={(position) => {
            onSave && onSave(bullet, position);
          }}
        />
      </CardContent>
    </Card>
  );
};

// Helper component for metric rows
const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px' }}>
      {label}
    </Typography>
    <MetricDots value={value} />
  </Box>
);

export default BulletCard;
